// DemoSession.ts
// -----------------------------------------------------------------------------
// Single source of truth for the FE-only "demo" session.
//
// When a demo session is active:
//   * AuthGuard lets the user through (we set `sessionTokenDev`, which it already
//     treats as a valid dev session).
//   * ApiService transparently routes every call to MockApiService (see the
//     Proxy at the bottom of ApiService.tsx) so NOTHING ever hits the backend.
//
// The flag lives in localStorage, so every existing `localStorage.clear()` call
// (real logout, LoginPage mount, mobile exit, 401 handler) already clears it.
// We also expose clearDemoSession() for the explicit "real login" safety net.
// -----------------------------------------------------------------------------

export const DEMO_FLAG_KEY = "demoSession";

// Namespace under which the mock "backend" persists all demo data in localStorage.
export const DEMO_NS = "demo:";

// Hardcoded demo credentials (also duplicated in public/demo_login.html).
export const DEMO_USER = "demouser";
export const DEMO_PASS = "demouser123";
export const DEMO_USER_EMAIL = "demouser@demo.local";

/** True when the current session is the mocked, FE-only demo session. */
export const isDemoSession = (): boolean =>
  typeof window !== "undefined" &&
  window.localStorage.getItem(DEMO_FLAG_KEY) === "true";

/**
 * Activates the demo session. Sets the routing flag plus the two keys the app
 * already relies on for an authenticated dev user, so AuthGuard and the header
 * work with zero changes.
 *
 * (public/demo_login.html performs the exact same writes inline, because a
 * static HTML page cannot import this module.)
 */
export const startDemoSession = (): void => {
  localStorage.setItem(DEMO_FLAG_KEY, "true");
  localStorage.setItem("sessionTokenDev", "DEMO-SESSION"); // satisfies AuthGuard
  localStorage.setItem("loggedUserId", DEMO_USER_EMAIL);
};

/**
 * Clears the demo flag and every piece of demo data persisted by the mock.
 * Safe to call at any time; used as a guard before any real authentication so a
 * returning user immediately gets the real backend again.
 */
export const clearDemoSession = (): void => {
  localStorage.removeItem(DEMO_FLAG_KEY);
  Object.keys(localStorage)
    .filter((k) => k.startsWith(DEMO_NS))
    .forEach((k) => localStorage.removeItem(k));
};